import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../../services/redux/types";
import { selectStorageState } from "../../../app/state/storageObjectSlice/selectors";
import { CasesValue } from "../../../services/api/instances/casesApi/types";
import {
  EMPTY_CASE_COMMON_INFO,
  INITIAL_AI_SCORING_OBJECT,
  INITIAL_CASE_AGENT_HARNESS,
  INITIAL_CASE_INFO,
  INITIAL_CASE_SOLUTION,
} from "../constants";
import { aggregateCaseLocalValue } from "../helpers/aggregateCaseLocalState";
import { buildCaseMarkdown } from "../helpers/buildCaseMarkdown";

export const selectCasesState = (state: RootState) => state.casesState;

export const selectAllCases = createSelector(
  selectStorageState,
  (storageState) => {
    const allCases = Object.values(storageState.cases.data.entities);
    if (!allCases) return EMPTY_CASE_COMMON_INFO;
    return allCases
      ?.map((object) =>
        object?.value
          ? (object.value as CasesValue)?.case_object?.case_info
          : null,
      )
      ?.filter((item) => item !== null);
  },
);

export const selectCaseByLlm = createSelector(selectCasesState, (state) => {
  return state.caseByLlm;
});

export const selectCaseMessages = createSelector(selectCasesState, (state) => {
  return state.messages;
});

export const selectCaseEntityMode = createSelector(
  selectCasesState,
  (state) => {
    return state.entityMode;
  },
);

export const selectCaseMessageLoading = createSelector(
  selectCasesState,
  (state) => {
    return state.isMessageLoading;
  },
);

export const selectCaseValue = createSelector(selectCasesState, (state) => {
  return state.caseValue;
});

export const selectCaseObjectMarkdown = createSelector(
  selectCaseValue,
  (state) => {
    if (!state?.case_object) return "";
    return buildCaseMarkdown(state.case_object);
  },
);

export const selectCaseComments = createSelector(selectCaseValue, (state) => {
  return state?.case_comments ?? [];
});

export const selectCaseAgentHarness = createSelector(
  selectCaseValue,
  (state) => state?.agent_harness ?? INITIAL_CASE_AGENT_HARNESS,
);

export const selectIsLocked = createSelector(selectCaseValue, (state) => {
  return state?.metadata?.is_locked ?? false;
});

export const selectCaseSolution = createSelector(selectCasesState, (state) => {
  return state.caseValue?.solution_prompt ?? INITIAL_CASE_SOLUTION;
});

export const selectCaseAiScoring = createSelector(selectCasesState, (state) => {
  return state.caseValue?.ai_scoring ?? INITIAL_AI_SCORING_OBJECT;
});

export const selectCaseObject = createSelector(selectCaseValue, (state) => {
  if (!state) return INITIAL_CASE_INFO.case_object;
  return state.case_object;
});

export const selectCaseValid = createSelector(selectCaseObject, (state) =>
  Boolean(
    state?.case_info?.task_description &&
      state?.case_artifacts_chain?.input_artifact &&
      state?.case_artifacts_chain?.output_artifact &&
      state?.case_artifacts_chain?.consumer &&
      state?.case_transform_method?.knowledge,
  ),
);

export const selectCaseFilledFields = createSelector(
  selectCaseObject,
  selectCaseValid,
  (state, isValid) => {
    if (!isValid) return null;

    return {
      task_description: state?.case_info?.task_description,
      input_artifact: state?.case_artifacts_chain?.input_artifact,
      output_artifact: state?.case_artifacts_chain?.output_artifact,
      consumer: state?.case_artifacts_chain?.consumer,
      knowledge: state?.case_transform_method?.knowledge,
      skills: state?.case_transform_method?.skills,
    };
  },
);

export const selectDirtyFields = createSelector(
  selectCasesState,
  (state) => state.dirtyFields,
);

export const selectAiExamples = createSelector(selectCasesState, (state) => {
  return state.aiExamples;
});

export const selectAiTips = createSelector(selectCasesState, (state) => {
  return state.aiTip;
});

export const selectCaseMode = createSelector(selectCasesState, (state) => {
  return state.caseMode;
});

export const selectIsTipLoading = createSelector(selectCasesState, (state) => {
  return state.isTipLoading;
});

export const selectAiExamplesById = createSelector(
  selectCasesState,
  (_state: RootState, id: string) => id,
  (state, id) => state.aiExamples[id] ?? null,
);

export const selectLocalCaseState = createSelector(
  selectCaseValue,
  selectDirtyFields,
  selectCaseEntityMode,
  (caseObj, dirtyFields, entityMode) =>
    aggregateCaseLocalValue(
      caseObj ?? INITIAL_CASE_INFO,
      dirtyFields,
      entityMode,
    ),
);

export const selectCasesLoading = createSelector(selectCasesState, (state) => {
  return state.isCasesLoading;
});

export const selectExamplesLoading = createSelector(
  selectCasesState,
  (state) => {
    return state.isExamplesLoading;
  },
);

export const selectSolutionLoading = createSelector(
  selectCasesState,
  (state) => {
    return state.isSolutionLoading;
  },
);
